/*
 * Copyright (c) 2021-2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: ProjectDataForm.tsx
 * Project: mle-client
 * Committed last: 2021/11/10 @ 815
 * ---
 */

import React, {useCallback, useContext, useState} from 'react';
import {Button, Divider, FormGroup, Intent, Radio, RadioGroup} from '@blueprintjs/core';
import {BpReadonlyWrapper} from './wrappers/BpReadonlyWrapper';
import {currentThemeIsDark, NameValue, objectToSelectItemsArray, valueToSelectObject} from '../../common/utils';
import {ColorModes, MeasureUnits} from '../../common/enums';
import {MleCustomSelect} from '../editor/select/common/MleCustomSelect';
import {Editor} from '../../store/observables/Editor';
import {User} from '../../entities';
import classNames from 'classnames';
import {IsoSizes, isoSizesPixelDimensions, Orientations} from '../../common/project';
import * as Yup from 'yup';
import {defaultRequiredMessage} from "../../config/content";
import {Form, Formik, FormikHelpers} from "formik";
import {BpTextFieldWrapper} from "./wrappers/BpTextFieldWrapper";
import {ToggleButton} from "../editor/buttons/ToggleButton";
import {mleDomainGqlClient} from "../../api/domain";
import {TITLE_AVAILABLE} from "../../api/domain/mutations.gql";
import {BpSelectFieldWrapper} from "./wrappers/BpSelectFieldWrapper";
import {ProjectCreationAgent} from "../../store/agents/ProjectCreationAgent";
import {useToggle} from "../../common/hooks";
import {ISOSizeSelect} from "../editor/select/project/ISOSizeSelect";
import {DialogContext} from "../editor/dialogs/create-project/CreateProjectDialog";
import {ReactToProp} from "../common/ReactToProp";
import {measureUnitToMeasureUnit, pixelToMeasureUnit} from "../../common/measure-unit-utils";

/**
 * Props da passare al form per modificare o creare un progetto
 */
interface ProjectDataFormProps {
    // Dati dell'utente
    user: User;
    // Puntatore all'work-area (utilizzato quando si vuole modificare la configurazione del progetto)
    editor?: Editor;
    // Indica se mostrare il pulsante submit
    showSubmit?: boolean;
    onSubmit: (values: ProjectDataFormValues) => any;
    // Funzione che riceve come parametro un oggetto per validare il form programmaticamente dall'esterno
    actionPerformer?: (action: FormActionRef) => void;
    // Valori iniziali
    initialValues?: ProjectDataFormValues;
    // Indica se il dialogo viene utilizzato per modificare un progetto
    // Se vuoto viene considerato come false
    projectEditing?: boolean;
}

export interface FormActionRef {
    submit: () => void;
}

export interface ProjectDataFormValues {
    title: string;
    width: number;
    height: number;
    measureUnit: MeasureUnits;
    colorProfile: ColorModes;

    [key: string]: any;
}

export const ProjectTitleRegex: RegExp = /^[a-zA-Z0-9_-]{2,30}$/;

/**
 * Form per la creazione o modifica dei dati di un progetto
 * @param props
 * @constructor
 */
export const ProjectDataForm = (props: ProjectDataFormProps) => {
    const useIsoSizes = useToggle();
    const [selectedSize, setSelectedSize] = useState<IsoSizes>();
    const [orientation, setOrientation] = useState<Orientations>(Orientations.PORTRAIT);
    const dialogContext = useContext(DialogContext);

    const ProjectFormValidationSchema = Yup.object().shape({
        title: Yup.string().required(defaultRequiredMessage).matches(ProjectTitleRegex, "Il titolo del progetto deve avere al massimo 30 caratteri e non deve contenere simboli")
            .test('title-available', 'Esiste già un\' etichetta con questo titolo', async (value): Promise<boolean> => {
                if (props.projectEditing === true) return true;

                if (!value) return false;

                try {
                    const res = await mleDomainGqlClient.mutate({
                        mutation: TITLE_AVAILABLE,
                        fetchPolicy: 'no-cache',
                        variables: {
                            title: value
                        }
                    });

                    return res.data.titleAvailable;
                } catch (e) {
                    return false;
                }
            }),
        width: Yup.number().required(defaultRequiredMessage).positive('Inserisci un numero positivo'),
        height: Yup.number().required(defaultRequiredMessage).positive('Inserisci un numero positivo'),
        measureUnit: Yup.string().required()
    })

    /**
     * Campi per inserire dimensioni personalizzate
     * @constructor
     */
    const CustomSizeInput = (p: { measureUnit: MeasureUnits }) => {
        return (
            <div className="mle-form-row w-2/3">
                <BpTextFieldWrapper inputClassName="w-2/3" name="width" label="Larghezza"
                                    labelInfo={p.measureUnit.toLowerCase()} type="number"
                                    tabIndex={2}/>
                <Button
                    small
                    minimal
                    icon="exchange"
                    intent={Intent.PRIMARY}
                />
                <BpTextFieldWrapper inputClassName="w-2/3" name="height" label="Altezza"
                                    labelInfo={p.measureUnit.toLowerCase()} type="number" tabIndex={3}/>
            </div>
        )
    }

    /**
     * Input per selezionare dimensioni predefinite
     * @param props
     * @constructor
     */
    const ISOSizeInput = (props: { width: number, height: number, measureUnit: MeasureUnits, helpers: Partial<FormikHelpers<ProjectDataFormValues>> }) => {

        const handleSizeSelect = useCallback(({name, value}) => {
            const setFieldValue = props.helpers.setFieldValue as (field: string, value: any) => void;

            setFieldValue('width', pixelToMeasureUnit(props.measureUnit, isoSizesPixelDimensions[value].width));
            setFieldValue('height', pixelToMeasureUnit(props.measureUnit, isoSizesPixelDimensions[value].height));
            setSelectedSize({name, value} as any);
        }, [setSelectedSize])

        return (
            <div>
                <FormGroup label="Dimensioni predefinite">
                    <ISOSizeSelect measureUnit={props.measureUnit} onSizeSelect={handleSizeSelect}
                                   initialValue={selectedSize}/>
                </FormGroup>
                <RadioGroup
                    onChange={event => {
                        setOrientation(event.currentTarget.value as unknown as Orientations)
                        const setFieldValue = props.helpers.setFieldValue as (field: string, value: any) => void;

                        // Swap
                        const tmp = props.width;
                        setFieldValue('width', props.height);
                        setFieldValue('height', tmp);
                    }}
                    inline
                    selectedValue={orientation}
                    label="Orientamento">
                    <Radio label="Verticale" value={Orientations.PORTRAIT}/>
                    <Radio label="Orizzontale" value={Orientations.LANDSCAPE}/>
                </RadioGroup>
                {
                    useIsoSizes &&
                    <div>
                        <Divider className="mt-2 mb-3"/>
                        <div>L&apos;etichetta avrà dimensioni <strong
                            className="text-bpblue-200">{props.width}x{props.height}{props.measureUnit}</strong>
                        </div>
                    </div>
                }
            </div>
        )
    }

    return (
        <div>
            <Formik
                initialValues={props.initialValues || ProjectCreationAgent.getDefaultProjectData()}
                validationSchema={ProjectFormValidationSchema}
                onSubmit={props.onSubmit}
            >
                {
                    ({
                         handleChange,
                         values,
                         handleSubmit,
                         setFieldValue,
                         isValid,
                     }) => {
                        return (
                            <Form>
                                <BpReadonlyWrapper
                                    value={`${props.user.firstName} ${props.user.lastName}`}
                                    label="Autore"
                                    labelInfo="(automatico)"
                                    icon="user"/>
                                <BpTextFieldWrapper
                                    name="title"
                                    label="Nome progetto"
                                    placeholder="NuovoProgetto"
                                    tabIndex={1}
                                    type="text"
                                    required/>
                                <BpSelectFieldWrapper name="measureUnit" label="Unità di misura" required
                                                      child={<MleCustomSelect<NameValue>
                                                          name="measureUnit"
                                                          icon="one-to-one"
                                                          renderText={item => item.name}
                                                          renderLabel={item => item.value === MeasureUnits.INCH ? 'in prova - non sicura' : ''}
                                                          initalValue={valueToSelectObject(props.initialValues?.measureUnit as string, MeasureUnits) || ProjectCreationAgent.getDefaultMeasureUnit()}
                                                          onSelect={(value: NameValue<MeasureUnits>) => {
                                                              const oldMeasure = values.measureUnit;
                                                              setFieldValue("width", measureUnitToMeasureUnit(oldMeasure, value.value, values.width))
                                                              setFieldValue("height", measureUnitToMeasureUnit(oldMeasure, value.value, values.height))
                                                              setFieldValue("measureUnit", value.value)
                                                          }}
                                                          items={objectToSelectItemsArray(MeasureUnits)}
                                                      />}/>
                                <ReactToProp<boolean> value={isValid} onChange={value => {
                                    dialogContext.setNextEnabled(value)
                                }}/>
                                <ReactToProp value={handleChange} onChange={value => {
                                    if (props.actionPerformer)
                                        props.actionPerformer({
                                            submit() {
                                                handleSubmit();
                                            }
                                        });
                                }}/>
                                <Divider className="mt-1 mb-4"/>
                                {
                                    !props.editor &&
                                    <ToggleButton checked={useIsoSizes.isTrue()}
                                                  onToggle={() => useIsoSizes.toggle()}
                                                  affirmativeText="Usa dimensioni personalizzate"
                                                  negativeText="Usa dimensioni predefinite"
                                                  affirmativeIcon="arrow-left"
                                                  negativeIcon="arrow-right"/>
                                }
                                <div
                                    className={classNames(
                                        'mt-2 mb-4 p-2 rounded flex flex-row align-middle gap-x-2',
                                        {
                                            'bg-bpdisabled-light': !currentThemeIsDark(),
                                            'bg-bpdisabled-dark': currentThemeIsDark()
                                        })}>
                                    {useIsoSizes.isTrue() ?
                                        <ISOSizeInput
                                            width={values.width}
                                            height={values.height}
                                            measureUnit={values.measureUnit}
                                            helpers={{setFieldValue}}/> :
                                        <CustomSizeInput measureUnit={values.measureUnit}/>}
                                </div>
                                {props.showSubmit === true && <div className="mle-form-submit">
                                    <Button
                                        type="submit"
                                        disabled={!isValid}
                                        intent="primary"
                                    >
                                        {props.editor ? 'Salva e applica' : 'Crea progetto'}
                                    </Button>
                                </div>}
                            </Form>
                        )
                    }
                }

            </Formik>
        </div>
    );
};
